import { Badge } from '@eventbrite/eds-badge';
import { gettext } from '@eventbrite/i18n';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import Countdown from 'react-countdown';
import { ZOOM_DEFAULT_STARTING_SOON_MINUTES } from '../../../constants';
import { getDateTime, isNone } from '../../../utils';
import { TimerRenderer } from '../Timer';

const INTERVAL_TO_CHECK_ZOOM_STATUS = 1000;

interface Props {
    data: object;
    event: { locale: string; start: any };
    onStatusChange: Function;
}

export class ZoomTimer extends PureComponent<Props> {
    isMounted = false;

    static propTypes = {
        data: PropTypes.object,
        event: PropTypes.object,
        onStatusChange: PropTypes.func,
    };

    state = {
        isCountingDown: false,
        isStarting: false,
        hasStarted: false,
        hasEnded: false,
    };

    componentDidMount() {
        this.isMounted = true;
        this._checkStatus();
    }

    componentWillUnmount() {
        this.isMounted = false;
    }

    _checkStatus = () => {
        if (!this.isMounted) return;

        const { isCountingDown, isStarting, hasStarted, hasEnded } = this.state;
        const { event, onStatusChange } = this.props;
        const joinButtonMinBefore = get(
            this.props.data,
            'zoomJoinButtonMinBefore.text',
            ZOOM_DEFAULT_STARTING_SOON_MINUTES,
        );

        const currentDateTime = getDateTime(event.locale);
        const startDateTime = this._getStartDateTime();
        const endDateTime = this._getEndDateTime();
        const minutesToStart = this._getStartDateTime().subtract(
            parseInt(joinButtonMinBefore),
            'minutes',
        );

        this.setState(
            {
                isCountingDown: currentDateTime.isBefore(minutesToStart),
                isStarting:
                    currentDateTime.isAfter(minutesToStart) &&
                    currentDateTime.isBefore(startDateTime),
                hasStarted:
                    currentDateTime.isAfter(startDateTime) &&
                    currentDateTime.isBefore(endDateTime),
                hasEnded: currentDateTime.isAfter(endDateTime),
            },
            onStatusChange({
                isCountingDown,
                isStarting,
                hasStarted,
                hasEnded,
            }),
        );

        // Every second we call this function to check if the status has changed.
        // Then clear the timeout if the meeting/webinar has ended.
        const timeout = setTimeout(() => {
            this._checkStatus();
        }, INTERVAL_TO_CHECK_ZOOM_STATUS);

        if (this.state.hasEnded) clearTimeout(timeout);
    };

    _getStartDateTime = () => {
        const { data, event } = this.props;

        let startTime = get(data, 'zoomStartDatetime.text', null);

        if (!startTime || startTime == 'None') {
            if (event?.start) {
                startTime = event.start.utc;
            }
        }

        return startTime && getDateTime(event?.locale, startTime);
    };

    _getEndDateTime = () => {
        const { data, event } = this.props;
        const duration = get(data, 'zoomDuration.text', null);
        if (!isNone(duration)) {
            const durationTime = parseInt(duration);
            return this._getStartDateTime().add(durationTime, 'minutes');
        } else {
            const endDateTime = get(event, 'end.utc', null);
            return getDateTime(event.locale, endDateTime);
        }
    };

    render() {
        const { hasStarted, hasEnded } = this.state;

        const startDateTime = this._getStartDateTime();
        let bgColor, content;

        if (hasEnded) {
            bgColor = 'ui-500';
            content = gettext('ENDED');
        } else if (hasStarted) {
            bgColor = 'primary-brand';
            content = gettext('LIVE');
        } else {
            bgColor = 'ui-700';
            content = (
                <Countdown
                    data-spec="component-countdown"
                    date={startDateTime.toString()}
                    daysInHours={true}
                    renderer={TimerRenderer}
                    zeroPadTime={2}
                />
            );
        }

        return (
            content && (
                <div className="zoom-timer eds-l-mar-left-2">
                    <Badge
                        data-spec="component-badge"
                        text={content}
                        bgColor={bgColor}
                    />
                </div>
            )
        );
    }
}
